import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  TimePicker,
  Row,
  Col,
  Space,
  Card,
  Layout,
  message,
  Spin,
  Avatar,
} from "antd";
import {
  LineChartOutlined,
  MenuOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import SideBar from "./_sideBar";
import axios from "axios";
import dayjs from "dayjs";
import CopyRight from "./CopyRight";
import { formatCurrency, formatNum } from "../../utlis/utlis";

const EnquiryChart = () => {
  const [menuDisabled] = useState(false);
  const [nextMenuDisabled] = useState(false);
  const [transactionList, setTransactionList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [merchant, setMerchant] = useState();
  const [subMerchant, setSubMerchant] = useState();
  const [selectedMerchantCodes, setSelectedMerchantCodes] = useState([]);
  const [loginUser, setLoginUser] = useState();
  const [form] = Form.useForm();
  const [searchData, setSearchData] = useState({
    stats: {},
    paymentChannelStats: [],
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const loginUserToken = JSON.parse(localStorage.getItem("loginUserToken"));
  const user = JSON.parse(localStorage.getItem("loginUser"));
  const pageSize = 10;

  useEffect(() => {
    setLoginUser(user);
    getMerchantData();

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [form]);

  const getMerchantData = async () => {
    if (user.userRole === "ADMIN") {
      // ADMIN: fetch all merchants and subMerchants, no auto-selection
      const res = await axios.get(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/merchant/findAllMerchantAndSubMerchant`,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        },
      );
      const data = res.data;

      // Map the data to include subMerchants within each merchant object
      const merchantsWithSubMerchants = data.map((item) => ({
        ...item.merchant,
        subMerchants: item.subMerchants || [],
      }));

      setMerchant(merchantsWithSubMerchants);
      setSubMerchant(data.flatMap((m) => m.subMerchants)); // Flattening all subMerchants
    } else {
      // MERCHANT or SUBMERCHANT: fetch only their merchant and subMerchant data
      const res = await axios.get(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/merchant/findMerchantAndSubMerchant/${user.merchantCode}`,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        },
      );
      const data = res.data;

      // Auto-fill merchant name for both MERCHANT and SUBMERCHANT
      form.setFieldsValue({
        merchantCode: data.merchant.merchantCode,
        merchantName: data.merchant.name,
      });

      // If user is SUBMERCHANT, also auto-fill submerchant
      if (user.subMerchantCode) {
        const subMerchantData = data.subMerchants.find(
          (s) => s.merchantCode === user.subMerchantCode,
        );
        form.setFieldsValue({
          subMerchantCode: subMerchantData?.merchantCode,
          subMerchantName: subMerchantData?.name,
        });
      }

      // For MERCHANT role, set merchant with subMerchants for filtering
      setMerchant([{ ...data.merchant, subMerchants: data.subMerchants }]);
      setSubMerchant(data.subMerchants);
      
      // For MERCHANT role, auto-select the merchant code for filtering
      if (!user.subMerchantCode) {
        setSelectedMerchantCodes([data.merchant.merchantCode]);
      }
    }
  };

  const { Content } = Layout;

  const handleSearch = async (
    values,
    page = 0,
    size = pageSize,
    resetPagination = false,
  ) => {
    try {
      setLoading(true);
      setDisabled(true);
      setTransactionList([]);

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = String(d.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const formatTime = (time) => {
        if (!time) return null;
        if (typeof time === "string") {
          return time; // Assume it's already in "HH:mm:ss" format
        }
        const t = new Date(time);
        if (isNaN(t.getTime())) {
          console.warn("Invalid time input:", time);
          return null; // Handle invalid date inputs
        }
        const hours = String(t.getHours()).padStart(2, "0");
        const minutes = String(t.getMinutes()).padStart(2, "0");
        const seconds = String(t.getSeconds()).padStart(2, "0");
        return `${hours}:${minutes}:${seconds}`;
      };

      const formattedValues = {
        ...values,
        merchantCode: Array.isArray(values.merchantCode)
          ? values.merchantCode
          : values.merchantCode
            ? [values.merchantCode]
            : null,
        subMerchantCode: Array.isArray(values.subMerchantCode)
          ? values.subMerchantCode
          : values.subMerchantCode
            ? [values.subMerchantCode]
            : null,
        orderDateFrom: values.orderDateFrom
          ? formatDate(values.orderDateFrom)
          : null,
        orderDateTo: values.orderDateTo ? formatDate(values.orderDateTo) : null,
        orderTimeFrom: values.orderTimeFrom
          ? formatTime(values.orderTimeFrom)
          : null,
        orderTimeTo: values.orderTimeTo ? formatTime(values.orderTimeTo) : null,
        invoiceNo: values.invoiceNo?.trim() || null,
        status:
          Array.isArray(values.status) && values.status.length > 0
            ? values.status
            : null,
        paymentChannel:
          Array.isArray(values.paymentChannel) &&
          values.paymentChannel.length > 0
            ? values.paymentChannel
            : null,
      };

      // setSearchValues(formattedValues);

      console.log("Chart formattedValues", formattedValues);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/enquiry/enquiryChartTransaction`,
        formattedValues,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        },
      );

      console.log("CHART RESPONSE", response.data);
      commonDataList(response.data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      message.error("Failed to fetch transactions. Please try again.");
    } finally {
      setLoading(false);
      setDisabled(false);
    }
  };

  const commonDataList = (data) => {
    setSearchData({
      transactionData: data.transactionData || [],
      stats: data.stats || {},
      paymentChannelStats: data.paymentChannelStats || [],
    });
  };

  const handleClear = () => {
    form.resetFields();
    getMerchantData();
    setTransactionList([]);
  };

  const paymentMethodsChartData = Object.values(
    searchData.paymentChannelStats.reduce((acc, item) => {
      if (!acc[item.paymentChannel]) {
        acc[item.paymentChannel] = { name: item.paymentChannel, pv: 0 };
      }
      acc[item.paymentChannel].pv += item.count;
      return acc;
    }, {}),
  ).sort((a, b) => b.pv - a.pv);

  const filteredSubMerchants = React.useMemo(() => {
    if (!merchant || !Array.isArray(merchant)) return [];
    if (!selectedMerchantCodes.length) return [];

    const filtered = merchant
      .filter((m) => selectedMerchantCodes.includes(m.merchantCode))
      .flatMap((m) => m.subMerchants || []);
    
    return filtered;
  }, [merchant, selectedMerchantCodes]);

  return (
    <Layout style={{ minHeight: "100vh", backgroundColor: "#f4f6f9" }}>
      <SideBar
        menuDisabled={menuDisabled}
        nextMenuDisabled={nextMenuDisabled}
        fontSize="14px"
        drawerVisibleValue={drawerVisible}
        setDrawerVisibleValue={setDrawerVisible}
      />

      <Layout className="site-layout">
        <Content>
          <div
            className="site-layout-background"
            style={{ padding: "10px", width: "100%" }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              {/* Loading Spinner */}
              {loading && (
                <Card
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    background: "#eef2ff",
                  }}
                >
                  <Spin size="large" />
                  <div style={{ marginTop: "10px" }}>
                    Searching transactions...
                  </div>
                </Card>
              )}

              {/* Search Form */}
              <Card
                title={
                  <span>
                    {isMobile && (
                      <MenuOutlined
                        onClick={() => setDrawerVisible((prev) => !prev)}
                        style={{ marginRight: 8, fontSize: 30 }}
                      />
                    )}
                    <LineChartOutlined
                      onClick={() => setDrawerVisible(true)}
                      style={{ marginRight: 8 }}
                    />
                    Review Summary
                  </span>
                }
                headStyle={{ backgroundColor: "#cccccc" }}
                extra={
                  <span>
                    {loginUser?.username}
                    <Avatar
                      src={loginUser?.avatarUrl}
                      icon={!loginUser?.avatarUrl && <UserOutlined />}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
              >
                <Form
                  layout="vertical"
                  onFinish={(values) => handleSearch(values, 0, 10, true)}
                  form={form}
                  style={{ maxWidth: "1000px", margin: "0 auto" }}
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="merchantCode" hidden>
                        <Input type="hidden" />
                      </Form.Item>

                      {loginUser?.userRole === "ADMIN" ? (
                        // ADMIN: Show dropdown to select merchants
                        <Form.Item label="Merchant" name="merchantCode">
                          <Select
                            mode="multiple"
                            placeholder="Select Merchant"
                            allowClear
                            onChange={(values) => {
                              console.log("selected merchants:", values);
                              setSelectedMerchantCodes(values);

                              form.setFieldsValue({
                                subMerchantCode: [],
                              });
                            }}
                          >
                            {merchant?.map((m) => (
                              <Select.Option
                                key={m.merchantCode}
                                value={m.merchantCode}
                              >
                                {m.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        // MERCHANT or SUBMERCHANT: Show disabled input with merchant name
                        <Form.Item label="Merchant" name="merchantName">
                          <Input disabled />
                        </Form.Item>
                      )}
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item name="subMerchantCode" hidden>
                        <Input type="hidden" />
                      </Form.Item>

                      {loginUser?.subMerchantCode ? (
                        // SUBMERCHANT: Show disabled input with submerchant name
                        <Form.Item label="SubMerchant" name="subMerchantName">
                          <Input disabled />
                        </Form.Item>
                      ) : (
                        // ADMIN or MERCHANT: Show dropdown to select submerchants
                        <Form.Item label="SubMerchant" name="subMerchantCode">
                          <Select
                            mode="multiple"
                            placeholder="Select SubMerchant"
                            allowClear
                            disabled={
                              loginUser?.userRole === "ADMIN" &&
                              selectedMerchantCodes.length === 0
                            }
                          >
                            {filteredSubMerchants?.map((s) => (
                              <Select.Option
                                key={s.merchantCode}
                                value={s.merchantCode}
                              >
                                {s.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      )}
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Order Date From"
                        name="orderDateFrom"
                        initialValue={dayjs().startOf("day")}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format={"YYYY-MM-DD"}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Order Date To"
                        name="orderDateTo"
                        initialValue={dayjs().startOf("day")}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format={"YYYY-MM-DD"}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Order Time From" name="orderTimeFrom">
                        <TimePicker
                          style={{ width: "100%" }}
                          format={"HH:mm:ss"}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item label="Order Time To" name="orderTimeTo">
                        <TimePicker
                          style={{ width: "100%" }}
                          format={"HH:mm:ss"}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Invoice Number/Order ID"
                        name="invoiceNo"
                      >
                        <Input placeholder="Enter invoice number or order ID" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item label="Product Description" name="description">
                        <Input placeholder="Enter Product Description" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Status" name="status">
                        <Select
                          mode="multiple"
                          placeholder="Select Status"
                          allowClear
                        >
                          <Select.Option value="SUCCESS">SUCCESS</Select.Option>
                          <Select.Option value="PENDING">PENDING</Select.Option>
                          <Select.Option value="IN_PROGRESS">
                            IN_PROGRESS
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item label="Payment Channel" name="paymentChannel">
                        <Select
                          mode="multiple"
                          placeholder="Select Channel"
                          allowClear
                        >
                          <Select.Option value="MMQR">
                            Myanmar Pay (MMQR)
                          </Select.Option>
                          <Select.Option value="KBZ_PAY">KBZ PAY</Select.Option>
                          <Select.Option value="AYA_PAY">AYA PAY</Select.Option>
                          <Select.Option value="CB_PAY">CB PAY</Select.Option>
                          <Select.Option value="OK_DOLLAR">
                            OK_DOLLAR
                          </Select.Option>
                          <Select.Option value="KBZ_USD">KBZ_USD</Select.Option>
                          <Select.Option value="KBZ_MPU">KBZ_MPU</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col>
                      <Button type="primary" htmlType="submit">
                        Search
                      </Button>
                    </Col>
                    <Col>
                      <Button onClick={handleClear}>Clear</Button>
                    </Col>
                  </Row>
                </Form>
              </Card>

              {/* Transaction Summary & Chart */}
              {searchData?.stats?.todayTotalMMKAmount !== undefined && (
                <Card
                  title="Transaction Summary"
                  style={{ borderRadius: "10px" }}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={8}>
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Transaction Amount (Excluding Bank Charges)
                          </span>
                        }
                        className="mb-3 shadow-sm border  "
                      >
                        <div className="col d-flex flex-column gap-3">
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  searchData?.stats?.todayTotalMMKAmount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2">USD</div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  searchData?.stats?.todayTotalUSDAmount || 0,
                                  "USD",
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Transactions Count
                          </span>
                        }
                        className="mb-3 shadow-sm border "
                      >
                        <div className="col d-flex flex-column gap-3">
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 fw-medium mb-0 ">
                                {formatNum(
                                  searchData?.stats?.todayMMKCount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2">USD</div>
                              <div className="h6 mb-0 fw-medium">
                                {formatNum(
                                  searchData?.stats?.todayUSDCount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Bank Charges Amount
                          </span>
                        }
                        className="shadow-sm border "
                      >
                        <div className="col d-flex flex-column gap-3">
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                MMK
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  searchData?.stats?.serviceFeeMMKAmount || 0,
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-center p-3 border rounded">
                              <div className="text-muted small mb-2 fw-medium">
                                USD
                              </div>
                              <div className="h6 mb-0 fw-medium">
                                {formatCurrency(
                                  searchData?.stats?.serviceFeeUSDAmount || 0,
                                  "USD",
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: "20px" }}>
                    <Col xs={24}>
                      <Card
                        title={
                          <span className="text-dark fw-bold">
                            Payment Methods Used
                          </span>
                        }
                        className="shadow-sm h-100"
                        bodyStyle={{ padding: "20px" }}
                      >
                        <div style={{ paddingTop: "100px" }}>
                          {(() => {
                            // Calculate max value once
                            const maxPaymentValue = Math.max(
                              ...paymentMethodsChartData.map((item) => item.pv),
                            );

                            return paymentMethodsChartData.map((method) => (
                              <div
                                key={method.name}
                                className="row align-items-center mb-3"
                              >
                                {/* Method Name */}
                                <div className="col-3 col-md-2">
                                  <div className="text-muted small fw-medium text-truncate">
                                    {method.name}
                                  </div>
                                </div>

                                {/* Progress Bar */}
                                <div className="col-6 col-md-8">
                                  <div
                                    className="progress"
                                    style={{ height: "32px" }}
                                  >
                                    <div
                                      className={`progress-bar "bg-blue-500"`}
                                      role="progressbar"
                                      style={{
                                        width: `${
                                          (method.pv / maxPaymentValue) * 100
                                        }%`,
                                      }}
                                      aria-valuenow={method.pv}
                                      aria-valuemin="0"
                                      aria-valuemax={maxPaymentValue}
                                    />
                                  </div>
                                </div>

                                {/* Value */}
                                <div className="col-3 col-md-1">
                                  <div className="text-end fw-medium text-dark">
                                    {method.pv.toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              )}
            </Space>
          </div>
          <CopyRight />
        </Content>
      </Layout>
    </Layout>
  );
};

export default EnquiryChart;
